import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  ListGroup,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { capitalize } from "../../../lib/display";
import {
  ReviewGroup,
  ReviewHousehold,
  ReviewPair,
  ReviewRow,
  ReviewSlantHousehold,
  SearchSide,
  apiMessage,
  useReviewSearch,
} from "./api";
import type { Change } from "./HouseholdMappingReview";

/** "A", "A and B", "A, B and C". */
function listOf(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

const PeopleBadges = ({ people }: { people: string[] }) => (
  <div>
    {people.map((name, index) => (
      <Badge
        key={`${name}-${index}`}
        bg="light"
        text="dark"
        className="me-1 border fw-normal"
      >
        {name}
      </Badge>
    ))}
  </div>
);

/** An AdviceCloud household: its name as a link, its accounts and CRM, its people. */
export const HouseholdSummary = ({
  household,
}: {
  household: ReviewHousehold;
}) => (
  <>
    <div className="fw-semibold">
      <Link
        to={`/clients/households/${household.id}`}
        target="_blank"
        rel="noreferrer"
      >
        {household.name}
      </Link>
      {/* Only "Already connected" shows an archived household, while "Show
          archived households" is on (R108). */}
      {household.archived && (
        <>
          {" "}
          <Badge bg="secondary" data-testid="archived-badge">
            Archived
          </Badge>
        </>
      )}
    </div>
    <div className="text-secondary small">
      AdviceCloud household · {household.accountCount} account
      {household.accountCount === 1 ? "" : "s"} ·{" "}
      {household.crm === null
        ? "not connected to a CRM"
        : `connected to ${capitalize(household.crm)}`}
    </div>
    <PeopleBadges people={household.people} />
  </>
);

/** A Slant household: its name, Client or Prospect, its people. */
export const SlantHouseholdSummary = ({
  slantHousehold,
}: {
  slantHousehold: ReviewSlantHousehold;
}) => (
  <>
    <div className="fw-semibold">{slantHousehold.name}</div>
    <div className="text-secondary small">
      Slant household · {slantHousehold.type}
    </div>
    <PeopleBadges people={slantHousehold.people} />
  </>
);

/**
 * What the two households of a pair share, and what a save of the pair does:
 * the CRM link that moves, each person that stays unconnected, each Slant
 * person that the save adds as a contact (R111), and the
 * "Household already mapped" text with its link when the pair cannot be saved.
 */
export const PairFacts = ({ pair }: { pair: ReviewPair }) => (
  <>
    <div>
      {pair.sharedPeople.map((person, index) => (
        <Badge key={`${person.name}-${index}`} bg="success" className="me-1">
          Same {person.by}: {person.name}
        </Badge>
      ))}
      {pair.strength === "name" && (
        <Badge bg="secondary" className="me-1">
          Similar household name
        </Badge>
      )}
      {pair.sharedPeople.length === 0 && pair.strength !== "name" && (
        <Badge bg="secondary" className="me-1">
          Shares no people
        </Badge>
      )}
    </div>
    {pair.alreadyMapped !== null ? (
      <Alert variant="danger" className="mt-2 mb-0 py-2">
        <strong>Household already mapped.</strong>{" "}
        {pair.alreadyMapped.householdId === pair.household.id
          ? pair.alreadyMapped.slantHouseholdName === null
            ? "This household is connected to a different Slant household."
            : `This household is connected to the Slant household "${pair.alreadyMapped.slantHouseholdName}".`
          : `The Slant household "${pair.slantHousehold.name}" is connected to a different household.`}{" "}
        <Link
          to={`/clients/households/${pair.alreadyMapped.householdId}`}
          target="_blank"
          rel="noreferrer"
        >
          Open {pair.alreadyMapped.householdName}
        </Link>
      </Alert>
    ) : (
      <>
        {pair.renamesTo !== null && (
          <Alert variant="warning" className="mt-2 mb-0 py-2">
            Accepting renames this household to &quot;{pair.renamesTo}&quot;.
          </Alert>
        )}
        {pair.movesFrom !== null && (
          <Alert variant="warning" className="mt-2 mb-0 py-2">
            Accepting moves this household from {capitalize(pair.movesFrom)} to
            Slant.
          </Alert>
        )}
        {pair.unconnectedPeople.length > 0 && (
          <Alert variant="warning" className="mt-2 mb-0 py-2">
            {listOf(pair.unconnectedPeople)}{" "}
            {pair.unconnectedPeople.length === 1 ? "has" : "have"} no record in
            the Slant household and will stay unconnected.
          </Alert>
        )}
        {pair.addedSlantPeople.length > 0 && (
          <Alert
            variant="info"
            className="mt-2 mb-0 py-2"
            data-testid="added-slant-people"
          >
            {listOf(pair.addedSlantPeople)}{" "}
            {pair.addedSlantPeople.length === 1 ? "has" : "have"} no contact in
            this household and will be added.
          </Alert>
        )}
        {pair.slantOnlyPeople.length > 0 && (
          <div className="text-secondary small mt-1">
            In the Slant household only: {listOf(pair.slantOnlyPeople)}.
          </div>
        )}
      </>
    )}
  </>
);

/**
 * The search of a row. `side` is the record of the row; the results are
 * records of the other kind, and `onPick` takes the one the advisor chooses.
 */
const RecordSearch = ({
  side,
  onPick,
}: {
  side: SearchSide;
  onPick: (pair: ReviewPair) => void;
}) => {
  const findsHouseholds = "slantHouseholdId" in side;
  const [query, setQuery] = useState("");
  const search = useReviewSearch(side);
  const label = findsHouseholds
    ? "Search households by household, person or email"
    : "Search Slant by household, person or email";
  const onSearch = () => {
    if (query.trim().length >= 2) search.mutate(query.trim());
  };

  return (
    <div className="mt-2">
      <InputGroup size="sm">
        <Form.Control
          aria-label={label}
          placeholder={label}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onSearch();
            }
          }}
        />
        <Button
          variant="outline-primary"
          onClick={onSearch}
          disabled={search.isPending || query.trim().length < 2}
        >
          {search.isPending ? "Searching…" : "Search"}
        </Button>
      </InputGroup>
      {search.isError && (
        <Alert variant="danger" className="mt-2 mb-0 py-2">
          {apiMessage(search.error, "Failed to search.")}
        </Alert>
      )}
      {search.isSuccess && search.data.length === 0 && (
        <div className="text-secondary small mt-2">No records found.</div>
      )}
      {search.isSuccess && search.data.length > 0 && (
        <ListGroup className="mt-2">
          {search.data.map((pair) => (
            <ListGroup.Item
              key={findsHouseholds ? pair.household.id : pair.slantHousehold.id}
              action
              onClick={() => onPick(pair)}
            >
              <span className="fw-semibold">
                {findsHouseholds
                  ? pair.household.name
                  : pair.slantHousehold.name}
              </span>{" "}
              <span className="text-secondary small ms-1">
                {pair.alreadyMapped !== null
                  ? "already connected"
                  : pair.sharedPeople.length > 0
                    ? `shares ${pair.sharedPeople.length} ${pair.sharedPeople.length === 1 ? "person" : "people"}`
                    : "shares no people"}
              </span>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

/** The badge and the Undo of a row that has a change. */
const ChangeStatus = ({
  change,
  onUndo,
  locked,
}: {
  change: Change;
  onUndo: () => void;
  locked?: boolean;
}) => (
  <>
    <Badge
      bg={
        change.kind === "connect"
          ? "success"
          : change.kind === "reject"
            ? "secondary"
            : "info"
      }
    >
      {change.kind === "connect"
        ? "Accepted"
        : change.kind === "reject"
          ? "None of these"
          : "Will be created"}
    </Badge>{" "}
    <Button variant="link" size="sm" disabled={locked} onClick={onUndo}>
      Undo
    </Button>
  </>
);

type RowProps = {
  rowKey: string;
  change: Change | undefined;
  canAccept: (key: string, pair: ReviewPair) => boolean;
  onChange: (change: Change) => void;
  onUndo: (key: string) => void;
  /** While a Save job runs: no action of the row adds or removes a change. */
  locked?: boolean;
};

/** Why Accept is disabled for a pair that is not already mapped. */
const InOtherChange = () => (
  <div className="text-secondary small mt-1">
    This record is in a change of a different row.
  </div>
);

/**
 * One Slant household with no link. In `clearMatch` it shows the one
 * household to accept and "None of these"; in `possibleMatches` the
 * candidates, "None of these" and "Create household"; in `noMatch`
 * "Create household".
 *
 * "None of these" is a pending change (`reject`), counted and revertable like
 * the others: the row leaves its group and shows in "No match", where
 * "Create household" is offered. A rejection alone writes nothing on Save.
 *
 * "Create household" on a `possibleMatches` row rejects each candidate of the
 * row in the same `create` change. The change has no `fromGroup`, so the row
 * stays in "Possible matches" (spec R21, R98, R99).
 */
export const SlantReviewRow = ({
  rowKey,
  row,
  group,
  change,
  canAccept,
  canCreate,
  onChange,
  onUndo,
  locked = false,
}: RowProps & {
  row: ReviewRow;
  group: ReviewGroup;
  /** Whether the Slant household can be created for the row `key`. */
  canCreate: (key: string, slantHouseholdId: string) => boolean;
}) => {
  const [showSearch, setShowSearch] = useState(false);
  // A household the advisor found with the search, in place of the candidates.
  const [picked, setPicked] = useState<ReviewPair | null>(null);
  // The candidate chosen with a radio: a household ID, or "none".
  const [selected, setSelected] = useState<number | "none" | null>(null);
  // A row whose candidates the advisor rejected shows in "No match".
  const rejectedFrom =
    change?.kind === "reject"
      ? change.fromGroup
      : change?.kind === "create"
        ? change.fromGroup
        : undefined;
  const rejected = rejectedFrom !== undefined;
  const reject = () =>
    onChange({
      kind: "reject",
      key: rowKey,
      row,
      fromGroup: group === "clearMatch" ? "clearMatch" : "possibleMatches",
    });

  const candidates = row.candidates;
  const hasCandidates = candidates.length > 0;
  const single = group === "clearMatch" ? candidates[0] : undefined;
  const pairToAccept: ReviewPair | undefined =
    picked ??
    single ??
    (typeof selected === "number"
      ? candidates.find((pair) => pair.household.id === selected)
      : undefined);
  const creatable =
    (!change && (!hasCandidates || group === "possibleMatches")) ||
    change?.kind === "reject";
  // The Slant household is in a connect of a household row.
  const createBlocked = !canCreate(rowKey, row.slantHousehold.id);
  const acceptable =
    pairToAccept !== undefined && canAccept(rowKey, pairToAccept);

  return (
    <ListGroup.Item data-testid={rowKey}>
      <Row>
        <Col md={4}>
          <SlantHouseholdSummary slantHousehold={row.slantHousehold} />
        </Col>
        <Col md={5}>
          {rejected && (
            <div className="text-secondary mb-1">
              None of these:{" "}
              {listOf(candidates.map((pair) => pair.household.name))}.
            </div>
          )}
          {change?.kind === "connect" ? (
            <>
              <HouseholdSummary household={change.pair.household} />
              <PairFacts pair={change.pair} />
            </>
          ) : change?.kind === "create" ? (
            <div className="text-secondary">
              Save creates a household with{" "}
              {row.slantHousehold.people.length === 1
                ? "1 person"
                : `${row.slantHousehold.people.length} people`}
              .
            </div>
          ) : picked !== null ? (
            <>
              <HouseholdSummary household={picked.household} />
              <PairFacts pair={picked} />
              <Button
                variant="link"
                size="sm"
                className="ps-0"
                onClick={() => setPicked(null)}
              >
                Clear this choice
              </Button>
            </>
          ) : single !== undefined ? (
            <>
              <HouseholdSummary household={single.household} />
              <PairFacts pair={single} />
            </>
          ) : rejected ? null : hasCandidates ? (
            <Form>
              {candidates.map((pair) => (
                <Form.Check
                  key={pair.household.id}
                  type="radio"
                  className="mb-2"
                  id={`${rowKey}-${pair.household.id}`}
                  name={rowKey}
                  checked={selected === pair.household.id}
                  onChange={() => setSelected(pair.household.id)}
                  label={
                    <>
                      <HouseholdSummary household={pair.household} />
                      <PairFacts pair={pair} />
                    </>
                  }
                />
              ))}
              <Form.Check
                type="radio"
                id={`${rowKey}-none`}
                name={rowKey}
                checked={selected === "none"}
                onChange={() => setSelected("none")}
                label="None of these"
              />
            </Form>
          ) : (
            <div className="text-secondary">No household found.</div>
          )}
          {pairToAccept !== undefined &&
            !change &&
            !acceptable &&
            pairToAccept.alreadyMapped === null && <InOtherChange />}
          {creatable && createBlocked && <InOtherChange />}
          {showSearch && !change && (
            <RecordSearch
              side={{ slantHouseholdId: row.slantHousehold.id }}
              onPick={(pair) => {
                setPicked(pair);
                setShowSearch(false);
              }}
            />
          )}
        </Col>
        <Col md={3} className="text-end">
          {change?.kind === "reject" ? (
            <>
              <Button
                size="sm"
                variant="outline-primary"
                className="me-1"
                disabled={locked || createBlocked}
                onClick={() =>
                  onChange({
                    kind: "create",
                    key: rowKey,
                    slantHousehold: row.slantHousehold,
                    rejectedHouseholdIds: candidates.map(
                      (pair) => pair.household.id,
                    ),
                    row,
                    fromGroup: change.fromGroup,
                  })
                }
              >
                Create household
              </Button>
              <ChangeStatus
                change={change}
                locked={locked}
                onUndo={() => onUndo(rowKey)}
              />
            </>
          ) : change ? (
            <ChangeStatus
              change={change}
              locked={locked}
              onUndo={() => onUndo(rowKey)}
            />
          ) : (
            <>
              {group === "possibleMatches" && picked === null ? (
                <Button
                  size="sm"
                  className="me-1"
                  disabled={
                    locked ||
                    selected === null ||
                    (selected !== "none" && !acceptable)
                  }
                  onClick={() => {
                    if (selected === "none") {
                      reject();
                    } else if (pairToAccept) {
                      onChange({
                        kind: "connect",
                        key: rowKey,
                        pair: pairToAccept,
                      });
                    }
                  }}
                >
                  Confirm
                </Button>
              ) : pairToAccept !== undefined ? (
                <Button
                  size="sm"
                  className="me-1"
                  disabled={locked || !acceptable}
                  onClick={() =>
                    onChange({
                      kind: "connect",
                      key: rowKey,
                      pair: pairToAccept,
                    })
                  }
                >
                  Accept
                </Button>
              ) : null}
              {single !== undefined && picked === null && (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="me-1"
                  disabled={locked}
                  onClick={reject}
                >
                  None of these
                </Button>
              )}
              {creatable && picked === null && (
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="me-1"
                  disabled={locked || createBlocked}
                  onClick={() =>
                    onChange({
                      kind: "create",
                      key: rowKey,
                      slantHousehold: row.slantHousehold,
                      rejectedHouseholdIds: candidates.map(
                        (pair) => pair.household.id,
                      ),
                    })
                  }
                >
                  Create household
                </Button>
              )}
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowSearch((shown) => !shown)}
              >
                {single !== undefined
                  ? "Choose a different household"
                  : "Search for a household"}
              </Button>
            </>
          )}
        </Col>
      </Row>
    </ListGroup.Item>
  );
};

/**
 * One AdviceCloud household with no link to Slant that no row offers. Its
 * search finds Slant households, the ones a household already holds too.
 */
export const HouseholdReviewRow = ({
  rowKey,
  household,
  change,
  canAccept,
  onChange,
  onUndo,
  locked = false,
}: RowProps & { household: ReviewHousehold }) => {
  const [showSearch, setShowSearch] = useState(false);
  const [picked, setPicked] = useState<ReviewPair | null>(null);
  const shown = change?.kind === "connect" ? change.pair : picked;
  const acceptable = picked !== null && canAccept(rowKey, picked);

  return (
    <ListGroup.Item data-testid={rowKey}>
      <Row>
        <Col md={4}>
          <HouseholdSummary household={household} />
        </Col>
        <Col md={5}>
          {shown ? (
            <>
              <SlantHouseholdSummary slantHousehold={shown.slantHousehold} />
              <PairFacts pair={shown} />
              {!change && (
                <Button
                  variant="link"
                  size="sm"
                  className="ps-0"
                  onClick={() => setPicked(null)}
                >
                  Clear this choice
                </Button>
              )}
            </>
          ) : (
            <div className="text-secondary">No Slant household found.</div>
          )}
          {picked !== null &&
            !change &&
            !acceptable &&
            picked.alreadyMapped === null && <InOtherChange />}
          {showSearch && !change && (
            <RecordSearch
              side={{ householdId: household.id }}
              onPick={(pair) => {
                setPicked(pair);
                setShowSearch(false);
              }}
            />
          )}
        </Col>
        <Col md={3} className="text-end">
          {change ? (
            <ChangeStatus
              change={change}
              locked={locked}
              onUndo={() => onUndo(rowKey)}
            />
          ) : (
            <>
              {picked !== null && (
                <Button
                  size="sm"
                  className="me-1"
                  disabled={locked || !acceptable}
                  onClick={() =>
                    onChange({ kind: "connect", key: rowKey, pair: picked })
                  }
                >
                  Accept
                </Button>
              )}
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowSearch((value) => !value)}
              >
                Search Slant
              </Button>
            </>
          )}
        </Col>
      </Row>
    </ListGroup.Item>
  );
};
